import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';

export const useAppSettings = () => {
  const [appSettings, setAppSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppSettings();
  }, []);

  const fetchAppSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('app_settings_mt2024')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setAppSettings(data);
        // Apply settings to the document
        applySettingsToDOM(data);
      } else {
        // Create default settings if none exist
        const defaultSettings = {
          appName: 'MedTracker',
          primaryColor: '#3b82f6',
          secondaryColor: '#8b5cf6',
          logoUrl: '',
          tagline: 'Medicine Management System',
          supportEmail: 'support@medtracker.com'
        };
        setAppSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error fetching app settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAppSettings = async (settings) => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('app_settings_mt2024')
        .upsert(settings)
        .select()
        .single();

      if (error) throw error;

      setAppSettings(data);
      applySettingsToDOM(data);
      
      toast.success('App settings updated successfully!');
      return data;
    } catch (error) {
      console.error('Error updating app settings:', error);
      toast.error('Failed to update app settings');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const applySettingsToDOM = (settings) => {
    if (!settings) return;

    // Update CSS custom properties for colors
    const root = document.documentElement;
    if (settings.primaryColor) {
      root.style.setProperty('--primary-color', settings.primaryColor);
    }
    if (settings.secondaryColor) {
      root.style.setProperty('--secondary-color', settings.secondaryColor);
    }

    // Update document title
    if (settings.appName) {
      document.title = `${settings.appName} - Medicine Management`;
    }

    // Update favicon if logo is provided
    if (settings.logoUrl) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = settings.logoUrl;
      }
    }
  };

  return {
    appSettings,
    loading,
    updateAppSettings,
    fetchAppSettings
  };
};